import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Pencil, AlertCircle } from 'lucide-react';

const DEV_ORIGIN = 'https://dev-backoffice.nulifespanrx.com';

/**
 * Resolve a pricing-sheet file path to an absolute URL.
 * On localhost or the shadcn preview environment the static files
 * don't exist � fall back to the dev server where the PDFs live.
 */
function resolveSheetUrl(filePath) {
    if (!filePath) return filePath;
    const { hostname } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isShadcn = hostname === 'shadcn-backoffice.nulifespanrx.com';
    if (isLocal || isShadcn) {
        // filePath may already be absolute � keep it; otherwise prefix dev origin
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
            return filePath;
        }
        return `${DEV_ORIGIN}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
    }
    return filePath;
}

function PricingSheetCard({ sheetData, editSheet }) {
    const [iframeError, setIframeError] = useState(false);

    const resolvedPath = resolveSheetUrl(sheetData?.filePath);

    // Append PDF viewer params to suppress toolbar, nav pane, and scrollbar
    const previewUrl = resolvedPath
        ? resolvedPath.split('#')[0] + '#toolbar=0&navpanes=0&scrollbar=0'
        : '';

    const handleImageOpen = (filePath) => {
        window.open(filePath, '_blank');
    };

    const handleEditSheet = (e) => {
        e.stopPropagation();
        editSheet();
    };

    return (
        <Card
            className="group overflow-hidden transition-all hover:shadow-lg cursor-pointer"
            onClick={() => handleImageOpen(resolvedPath)}
        >
            <div className="relative h-44 bg-gray-50">
                {!iframeError ? (
                    <iframe
                        src={previewUrl}
                        className="w-full h-full pointer-events-none"
                        title={sheetData.fileName}
                        onError={() => setIframeError(true)}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-2">
                        <AlertCircle className="h-12 w-12 text-muted-foreground/70" />
                        <p className="text-sm text-muted-foreground font-medium">File not found</p>
                    </div>
                )}

                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none">
                    <div className="bg-accent rounded-full p-3 group-hover:scale-110 transition-transform">
                        <FileText className="h-6 w-6 text-white" />
                    </div>
                </div>

                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-background hover:bg-muted text-primary"
                        onClick={handleEditSheet}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <CardContent className="p-3">
                <h4 className="text-md font-bold text-primary truncate" title={sheetData.fileName}>
                    {sheetData.fileName}
                </h4>
            </CardContent>
        </Card>
    );
}

export default PricingSheetCard;

